import authenticatedAxiosInstance from './authenticatedAxiosInstance.service';
import authHeader from './auth-header';

const API_URL = process.env.REACT_APP_API_URL + '/admin';

class AdminService {
  getUsers(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/users', { headers: authHeader(), signal });
  }

  getUser(id: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + `/users/${id}`, {
      headers: authHeader(),
      signal,
    });
  }

  createUser(userData: any) {
    return authenticatedAxiosInstance.post(API_URL + '/users', userData, { headers: authHeader() });
  }

  inviteUser(id: string) {
    return authenticatedAxiosInstance.post(
      API_URL + `/users/${id}/invite`,
      {},
      { headers: authHeader() }
    );
  }

  updateUser(id: string, userData: any) {
    return authenticatedAxiosInstance.put(API_URL + `/users/${id}`, userData, {
      headers: authHeader(),
    });
  }

  deleteUser(id: string) {
    return authenticatedAxiosInstance.delete(API_URL + `/users/${id}`, { headers: authHeader() });
  }

  getCompanies(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/companies', {
      headers: authHeader(),
      signal,
    });
  }

  getRoles(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/roles', { headers: authHeader(), signal });
  }

  resetUserPassword(userId: string) {
    return authenticatedAxiosInstance.post(
      `${API_URL}/users/${userId}/reset-password`,
      {},
      { headers: authHeader() }
    );
  }

  requestPasswordReset(email: string) {
    // This endpoint doesn't use authHeader since users aren't logged in
    return authenticatedAxiosInstance.post(
      `${process.env.REACT_APP_API_URL}/auth/forgot-password`,
      { email }
    );
  }

  getDashboardUsers(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/users', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardCompanies(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/companies', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardCompany(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/company', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardLoans(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/loans', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardFloorplans(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/floorplans', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardCollections(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/collections', {
      headers: authHeader(),
      signal,
    });
  }

  getDashboardImports(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/dashboard/imports', {
      headers: authHeader(),
      signal,
    });
  }

  getReleaseNotes(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/release-notes', {
      headers: authHeader(),
      signal,
    });
  }

  getHistoricalReleaseNotes(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/release-notes/history', {
      headers: authHeader(),
      signal,
    });
  }

  getJobLogs(searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/log', {
      headers: authHeader(),
      params: { searchText },
      signal,
    });
  }

  getJobLogByRecId(RecId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + `/log/${RecId}`, {
      headers: authHeader(),
      signal,
    });
  }

  getSequenceLogs(searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/sequence-log', {
      headers: authHeader(),
      params: { searchText },
      signal,
    });
  }

  getSequenceLogByRecId(RecId: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + `/sequence-log/${RecId}`, {
      headers: authHeader(),
      signal,
    });
  }

  // FTP Testing methods
  getFtpConnections(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/ftp-connections', {
      headers: authHeader(),
      signal,
    });
  }

  testFtpConnection(connectionId: string, testMode: boolean) {
    return authenticatedAxiosInstance.post(
      API_URL + '/ftp-connections/test',
      { connectionId, testMode },
      { headers: authHeader() }
    );
  }

  testAllFtpConnections(testMode: boolean) {
    return authenticatedAxiosInstance.post(
      API_URL + '/ftp-connections/test-all',
      { testMode },
      { headers: authHeader() }
    );
  }

  getTestServerHealth(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/test-server-health', {
      headers: authHeader(),
      signal,
    });
  }

  // Test File Generator methods
  getImportTypes(signal?: AbortSignal): Promise<any> {
    return authenticatedAxiosInstance.get(API_URL + '/test-files/types', {
      headers: authHeader(),
      signal,
    });
  }

  async generateTestFile(
    params: {
      importType: string;
      count?: number;
      seed?: number;
      compId?: number;
      dmsFormat?: string;
    },
    signal?: AbortSignal
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await authenticatedAxiosInstance.post(
      API_URL + '/test-files/generate',
      params,
      {
        headers: authHeader(),
        responseType: 'arraybuffer',
        signal,
      }
    );
    const contentDisposition = (response.headers['content-disposition'] as string) || '';
    const match = contentDisposition.match(/filename="([^"]+)"/);
    const filename = match ? match[1] : `test-${params.importType}-${Date.now()}.xlsx`;
    const blob = new Blob([response.data as ArrayBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return { blob, filename };
  }

  getDatabaseInfo(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/database-info', {
      headers: authHeader(),
      signal,
    });
  }

  getDatabaseTables(_searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/database-info/tables', {
      headers: authHeader(),
      signal,
    });
  }

  getDatabaseViews(_searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/database-info/views', {
      headers: authHeader(),
      signal,
    });
  }

  getDatabaseRoutines(_searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/database-info/routines', {
      headers: authHeader(),
      signal,
    });
  }

  getDatabaseIndexes(_searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/database-info/indexes', {
      headers: authHeader(),
      signal,
    });
  }

  getDocuSignHealth(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/docusign/health', {
      headers: authHeader(),
      signal,
    });
  }

  getDocuSignWebhookLog(
    params: {
      page?: number;
      size?: number;
      envelopeId?: string;
      status?: string;
      from?: string;
      to?: string;
    },
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(API_URL + '/docusign/webhook-log', {
      headers: authHeader(),
      params,
      signal,
    });
  }

  deleteDocuSignWebhookLogEntry(id: number) {
    return authenticatedAxiosInstance.delete(API_URL + `/docusign/webhook-log/${id}`, {
      headers: authHeader(),
    });
  }

  migratePortalUsers(companyId?: string) {
    return authenticatedAxiosInstance.post(
      API_URL + '/users/migrate-portal-users',
      companyId ? { companyId } : {},
      { headers: authHeader() }
    );
  }

  getBlackBookSummary(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/blackbook/summary', {
      headers: authHeader(),
      signal,
      timeout: 60000,
    });
  }

  getBlackBookLookups(searchText?: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/blackbook/lookups', {
      headers: authHeader(),
      params: { searchText },
      signal,
    });
  }

  // Report Templates methods
  getReportTemplates(signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + '/report-templates', {
      headers: authHeader(),
      signal,
    });
  }

  getReportTemplateDetail(blobName: string, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(
      API_URL + `/report-templates/${encodeURIComponent(blobName)}`,
      { headers: authHeader(), signal }
    );
  }

  testGenerateReport(
    blobName: string,
    outputType: string = 'pdf',
    params?: {
      compId?: number;
      orgId?: number;
      locId?: number;
      dateBeg?: string;
      dateEnd?: string;
      amount?: number;
      amountFrom?: number;
      amountTo?: number;
      number?: number;
      numberFrom?: number;
      numberTo?: number;
    }
  ) {
    return authenticatedAxiosInstance.post(
      API_URL + '/report-templates/test-generate',
      { blobName, outputType, params: params || {} },
      { headers: authHeader(), timeout: 90000 }
    );
  }

  // Imports Monitor methods
  listImports(
    params: {
      startDate?: string;
      endDate?: string;
      importWhat?: string;
      status?: string;
      importBy?: string;
      compId?: number;
      limit?: number;
      offset?: number;
    } = {},
    signal?: AbortSignal
  ) {
    return authenticatedAxiosInstance.get(API_URL + '/imports-monitor/list', {
      headers: authHeader(),
      params,
      signal,
    });
  }

  inspectImport(logid: number, signal?: AbortSignal) {
    return authenticatedAxiosInstance.get(API_URL + `/imports-monitor/${logid}/inspect`, {
      headers: authHeader(),
      signal,
    });
  }

  undoImport(logid: number) {
    return authenticatedAxiosInstance.post(
      API_URL + `/imports-monitor/${logid}/undo`,
      {},
      { headers: authHeader(), timeout: 120000 }
    );
  }
}

export default new AdminService();
